import { SavedCaptionDesignSystemConfig } from "../../../remotion/Components/Captions/Caption";
import { FieldContainer } from './FieldContainer';
import { FontSelect } from './FontSelect';
import { IconDropdown } from '../LayoutConfig/IconDropdown';
import { SquareBorderIcon, SmoothBorderIcon, RoundedBorderIcon } from './../FrameConfig/BorderRadiusIcons';
import { ClassicCaptionPreview } from '../../../remotion/Components/Captions/ClassicCaptions/ClassicCaptionPreview';
import { CaptionPage } from '../../../remotion/Components/Captions/CaptionGrouper';
import { VideoCanvas, Position } from '../../../remotion/Components/DesignSystem';
import { useEffect, useMemo, useState } from 'react';
import { ThemeSystemConfig } from "../../../remotion/Components/Theme/Theme";
import TrebbleCard from "../../../../common/TrebbleCard";
import TrebbleSelect from "../../../../common/TrebbleSelect";
import TrebbleRadio, { TrebbleRadioChangeEvent } from "../../../../common/TrebbleRadio";
import TrebbleColorPicker, { TrebbleColorPickerValue } from "../../../../common/TrebbleColorPicker";
import TrebbleSwitch from "../../../../common/TrebbleSwitch";
import TrebbleInput from "../../../../common/TrebbleInput";
import ti18n from 'i18n!nls/ClipCreatori18n';

// Extend Window interface if not already done globally
declare global {
    interface Window {
      getI18n: (i18nData: any, key: string, options?: any) => string;
    }
  }

const CAPTION_POSITIONS = Object.freeze({
    TOP: {
        value: "top",
        label: window.getI18n(ti18n, "POSITION_TOP"),
    },
    MIDDLE: {
        value: "center",
        label: window.getI18n(ti18n, "POSITION_MIDDLE"),
    },
    BOTTOM: {
        value: "bottom",
        label: window.getI18n(ti18n, "POSITION_BOTTOM"),
    },
});



const FONT_OUTLINE = Object.freeze({
    NONE: {
        value: "none",
        label: window.getI18n(ti18n, "STYLE_NONE"),
    },
    SHADOW: {
        value: "shadow",
        label: window.getI18n(ti18n, "FONT_OUTLINE_SHADOW"),
    },
    GLOW: {
        value: "glow",
        label: window.getI18n(ti18n, "FONT_OUTLINE_GLOW"),
    },
    STROKE: {
        value: "stroke",
        label: window.getI18n(ti18n, "FONT_OUTLINE_STROKE"),
    },
    "3D_SHADOW": {
        value: "3d-shadow",
        label: window.getI18n(ti18n, "FONT_OUTLINE_3D_SHADOW"),
    },
    "3D_STROKE":{
        value:'3d-stroke',
        label: window.getI18n(ti18n, "FONT_OUTLINE_3D_STROKE")
    }
});

const FONT_BACKGROUND_STYLE = Object.freeze({
    NONE: {
        value: "none",
        label: window.getI18n(ti18n, "STYLE_NONE"),
    },
    TRANSLUCENT: {
        value: "translucent",
        label: window.getI18n(ti18n, "FONT_BACKGROUND_TRANSLUCENT"),
    },
    WHITE_BACKGROUND: {
        value: "white",
        label: window.getI18n(ti18n, "FONT_BACKGROUND_WHITE"),
    },
    COLORED_BACKGROUND: {
        value: "colored",
        label: window.getI18n(ti18n, "FONT_BACKGROUND_COLORED"),
    },
});

const CAPTION_LINE_CONFIG = Object.freeze({
    ONE_WORD: {
        value: "one_word",
        label: window.getI18n(ti18n, "LINES_ONE_WORD"),
    },
    ONE_LINE: {
        value: "one_line",
        label: window.getI18n(ti18n, "LINES_ONE_LINE"),
    },
    TWO_LINE: {
        value: "two_lines",
        label: window.getI18n(ti18n, "LINES_TWO_LINES"),
    },
    THREE_LINE: {
        value: "three_lines",
        label: window.getI18n(ti18n, "LINES_THREE_LINES"),
    },
});

const CAPTION_ANIMATIONS = Object.freeze({
    NO_ANIMATION: {
        value: "none",
        label: window.getI18n(ti18n, "ANIMATION_NONE"),
    },
    HIGHLIGHT: {
        value: "highlight",
        label: window.getI18n(ti18n, "ANIMATION_HIGHLIGHT"),
    },
    BOXED: {
        value: "boxed",
        label: window.getI18n(ti18n, "ANIMATION_BOXED"),
    },
    POP: {
        value: "pop",
        label: window.getI18n(ti18n, "ANIMATION_POP"),
    },
    SCALE: {
        value: "scale",
        label: window.getI18n(ti18n, "ANIMATION_SCALE"),
    },
    REVEAL: {
        value: "reveal",
        label: window.getI18n(ti18n, "ANIMATION_REVEAL"),
    },
    REVEAL_UP: {
        value: "reveal_up",
        label: window.getI18n(ti18n, "ANIMATION_REVEAL_UP"),
    },
    KARAOKE: {
        value: "karaoke",
        label: window.getI18n(ti18n, "ANIMATION_KARAOKE"),
    },
    REVEAL_SCALE: {
        value: "reveal_scale",
        label: window.getI18n(ti18n, "ANIMATION_REVEAL_SCALE"),
    },
    REVEAL_BOX: {
        value: "reveal_box",
        label: window.getI18n(ti18n, "ANIMATION_REVEAL_BOX"),
    },
    KARAOKE_SCALE: {
        value: "karaoke_scale",
        label: window.getI18n(ti18n, "ANIMATION_KARAOKE_SCALE"),
    },
    KARAOKE_BOX: {
        value: "karaoke_box",
        label: window.getI18n(ti18n, "ANIMATION_KARAOKE_BOX"),
    },
});

const FONT_SIZES = Object.freeze({
    SMALL: {
        value: "small",
        label: window.getI18n(ti18n, "FONT_SIZE_SMALL"),
    },
    MEDIUM: {
        value: "medium",
        label: window.getI18n(ti18n, "FONT_SIZE_MEDIUM"),
    },
    LARGE: {
        value: "large",
        label: window.getI18n(ti18n, "FONT_SIZE_LARGE"),
    },
    EXTRA_LARGE: {
        value: "extra_large",
        label: window.getI18n(ti18n, "FONT_SIZE_EXTRA_LARGE"),
    },
});

const CAPTION_ALIGNMENTS = Object.freeze({
    LEFT: { value: "left", label: window.getI18n(ti18n, "ALIGN_LEFT") },
    CENTER: { value: "center", label: window.getI18n(ti18n, "ALIGN_CENTER") },
    RIGHT: { value: "right", label: window.getI18n(ti18n, "ALIGN_RIGHT") },
});

const CAPTION_CASES = Object.freeze({
    STANDARD: {
        value: "standard",
        label: window.getI18n(ti18n, "CASE_STANDARD"),
    },
    UPPERCASE: {
        value: "uppercase",
        label: window.getI18n(ti18n, "CASE_UPPERCASE"),
    },
});

const BORDER_CORNER_RADIUS = Object.freeze({
    SQUARE: {
        value: 0,
        label: window.getI18n(ti18n, "BORDER_RADIUS_SQUARE"),
        icon: <SquareBorderIcon />
    },
    SMOOTH: {
        value: 0.10,
        label: window.getI18n(ti18n, "BORDER_RADIUS_SMOOTH"),
        icon: <SmoothBorderIcon />
    },
    ROUNDED: {
        value: 0.30,
        label: window.getI18n(ti18n, "BORDER_RADIUS_ROUNDED"),
        icon: <RoundedBorderIcon />
    },
});

interface CaptionEditFormProps {
    caption: SavedCaptionDesignSystemConfig;
    onCaptionChange: (caption: SavedCaptionDesignSystemConfig) => void;
    themeConfig?: ThemeSystemConfig;
}

// Preview text configurations
const PREVIEW_TEXTS = {
    one_word: [window.getI18n(ti18n, "PREVIEW_WORD")],
    one_line: [window.getI18n(ti18n, "PREVIEW_ONE_LINE")],
    two_lines: [window.getI18n(ti18n, "PREVIEW_TWO_LINES_1"), window.getI18n(ti18n, "PREVIEW_LINE_2")],
    three_lines: [window.getI18n(ti18n, "PREVIEW_TWO_LINES_1"), window.getI18n(ti18n, "PREVIEW_LINE_2"), window.getI18n(ti18n, "PREVIEW_THREE_LINES_3")]
} as const;

const PREVIEW_DURATION = 3000; // 3 seconds in milliseconds

export const CaptionEditForm = ({ caption, onCaptionChange, themeConfig }: CaptionEditFormProps) => {
    const [localCaption, setLocalCaption] = useState<SavedCaptionDesignSystemConfig>({ ...caption });
    const [highlightColorHex, setHighlightColorHex] = useState<string>(caption.captionConfig.highlightColor);
    const [mainColorHex, setMainColorHex] = useState<string>(caption.captionConfig.mainColor);

    useEffect(() => {
        if (localCaption.captionConfig.useDesignSystemColor && themeConfig?.brandConfig?.primaryColor) {
            setMainColorHex(themeConfig.brandConfig.primaryColor);
        }
    }, [themeConfig, localCaption.captionConfig.useDesignSystemColor]);

    const handleFieldChange = (field: string, value: any) => {
        setLocalCaption(prev => {
            const updatedCaption = {
                ...prev,
                captionConfig: {
                    ...prev.captionConfig,
                    [field]: value
                }
            };
            onCaptionChange(updatedCaption);
            return updatedCaption;
        });
    };

    const updateMainColor = (colorValue: string) => {
        setMainColorHex(colorValue);
        handleFieldChange('mainColor', colorValue);
    };

    const updateHighlightColor = (colorValue: string) => {
        setHighlightColorHex(colorValue);
        handleFieldChange('highlightColor', colorValue);
    };

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const name = e.target.value;
        setLocalCaption(prev => {
            const updatedCaption = {
                ...prev,
                name
            };
            onCaptionChange(updatedCaption);
            return updatedCaption;
        });
    };

    // Create a sample caption page for preview
    const previewPage = useMemo(() => {
        const lines = PREVIEW_TEXTS[localCaption.captionConfig.maxLines as keyof typeof PREVIEW_TEXTS];
        const timePerLine = PREVIEW_DURATION / lines.length;
        
        const tokens = lines.map((line, index) => ({
            text: line,
            fromMs: index * timePerLine,
            toMs: (index + 1) * timePerLine
        }));
        
        return {
            text: lines.join('\n'),
            tokens,
            startMs: 0,
            endMs: PREVIEW_DURATION
        } as CaptionPage;
    }, [localCaption.captionConfig.maxLines]);

    // Set up the preview canvas configuration
    const previewCanvas: VideoCanvas = {
        width: 400,
        height: 200,
        aspectRatio: "16:9",
        safeZone: {
            top: 0.1,
            bottom: 0.1,
            left: 0.1,
            right: 0.1
        }
    };

    
    return (
        <div className="flex flex-col h-full">

<TrebbleCard size="small" className="sticky top-0 z-20 bg-white">
                <FieldContainer 
                    label={window.getI18n(ti18n, "PREVIEW_TITLE")}
                    description={window.getI18n(ti18n, "PREVIEW_DESCRIPTION")}
                >
                    <div className="flex flex-col">
                        <ClassicCaptionPreview
                            page={previewPage}
                            fontFamily={localCaption.captionConfig.font}
                            canvas={previewCanvas}
                            maxLines={localCaption.captionConfig.maxLines}
                            primaryColor={localCaption.captionConfig.useDesignSystemColor? themeConfig?.brandConfig?.primaryColor || mainColorHex : mainColorHex}
                            currentSpokenWordColor={localCaption.captionConfig.useDesignSystemColor? themeConfig?.brandConfig?.accentColor|| highlightColorHex : highlightColorHex}
                            maxFontSize={Number(localCaption.captionConfig.fontSizeRatio) || 48}
                            outline={localCaption.captionConfig.outline}
                            background={localCaption.captionConfig.background}
                            textAnimation={localCaption.captionConfig.textAnimation}
                            borderRadius={localCaption.captionConfig.borderRadius}
                            captionCase={localCaption.captionConfig.captionCase}
                            hidePunctuation={localCaption.captionConfig.hidePunctuation}
                            position={localCaption.captionConfig.position as Position}
                            compact={true}
                        />
                    </div>
                </FieldContainer>
            </TrebbleCard>


            <div className="overflow-y-auto flex-1">
                {/* Basic Settings Card */}
                <TrebbleCard size="small" title={window.getI18n(ti18n, "BASIC_SETTINGS_TITLE")} className="mt-2">
                    <FieldContainer 
                        label={window.getI18n(ti18n, "CAPTION_NAME_LABEL")}
                        description={window.getI18n(ti18n, "CAPTION_NAME_DESCRIPTION")}
                    >
                        <TrebbleInput
                            className="w-full p-2 border rounded"
                            value={localCaption.name}
                            onChange={handleNameChange}
                            placeholder={window.getI18n(ti18n, "CAPTION_NAME_PLACEHOLDER")}
                        />
                    </FieldContainer>

                    <FieldContainer label={window.getI18n(ti18n, "FONT_LABEL")}>
                        <FontSelect 
                            className="w-full"
                            value={localCaption.captionConfig.font}
                            onChange={(newFont) => handleFieldChange('font', newFont)}
                        />
                    </FieldContainer>

                    <FieldContainer label={window.getI18n(ti18n, "FONT_SIZE_LABEL")}>
                        <TrebbleSelect
                            className="w-full"
                            options={Object.values(FONT_SIZES)}
                            value={localCaption.captionConfig.fontSizeRatio}
                            onChange={(newSize) => handleFieldChange('fontSizeRatio', newSize)}
                        />
                    </FieldContainer>
                </TrebbleCard>

                {/* Appearance Card */}
                <TrebbleCard size="small" title={window.getI18n(ti18n, "APPEARANCE_TITLE")} className="mt-4">
                    <FieldContainer label={window.getI18n(ti18n, "FONT_OUTLINE_LABEL")}>
                        <TrebbleSelect
                            className="w-full"
                            options={Object.values(FONT_OUTLINE)}
                            value={localCaption.captionConfig.outline}
                            onChange={(newOutline) => handleFieldChange('outline', newOutline)}
                        />
                    </FieldContainer>

                    <FieldContainer label={window.getI18n(ti18n, "FONT_BACKGROUND_LABEL")}>
                        <TrebbleSelect
                            className="w-full"
                            options={Object.values(FONT_BACKGROUND_STYLE)}
                            value={localCaption.captionConfig.background}
                            onChange={(newBackground) => handleFieldChange('background', newBackground)}
                        />
                    </FieldContainer>

                    <FieldContainer label={window.getI18n(ti18n, "BORDER_RADIUS_LABEL")}>
                        <IconDropdown
                            className="w-full"
                            options={Object.values(BORDER_CORNER_RADIUS)}
                            value={localCaption.captionConfig.borderRadius}
                            onChange={(value) => handleFieldChange('borderRadius', value)}
                        />
                    </FieldContainer>
                </TrebbleCard>

                {/* Colors Card */}
                <TrebbleCard size="small" title={window.getI18n(ti18n, "COLORS_TITLE")} className="mt-4">
                    <FieldContainer label={window.getI18n(ti18n, "USE_THEME_COLOR_LABEL")} direction="row">
                        <TrebbleSwitch
                            size="small"
                            checked={localCaption.captionConfig.useDesignSystemColor}
                            onChange={(checked: boolean) => handleFieldChange('useDesignSystemColor', checked)}
                        />
                    </FieldContainer>
                    {localCaption.captionConfig.useDesignSystemColor && !themeConfig?.brandConfig?.primaryColor && (
                        <div className="my-2" style={{ color: 'var(--error)' }}>{window.getI18n(ti18n, "NO_THEME_SELECTED_WARNING")}</div>
                    )}
                    <div className="flex items-center justify-between">
                        <div className="flex flex-col items-center">
                            <TrebbleColorPicker
                                className="w-8"
                                value={localCaption.captionConfig.useDesignSystemColor? themeConfig?.brandConfig?.primaryColor: mainColorHex}
                                disabled={localCaption.captionConfig.useDesignSystemColor}
                                onChangeComplete={(newColor: TrebbleColorPickerValue) => updateMainColor(newColor.toHexString())}
                                showText={false}
                            />
                            <span className="mt-1 opacity-50">{window.getI18n(ti18n, "MAIN_COLOR_LABEL")}</span>
                        </div>
                        <div className="flex flex-col items-center">
                            <TrebbleColorPicker
                                className="w-8"
                                value={localCaption.captionConfig.useDesignSystemColor? themeConfig?.brandConfig?.accentColor: highlightColorHex}
                                disabled={localCaption.captionConfig.useDesignSystemColor}
                                onChangeComplete={(newColor: TrebbleColorPickerValue) => updateHighlightColor(newColor.toHexString())}
                                showText={false}
                            />
                            <span className="mt-1 opacity-50">{window.getI18n(ti18n, "HIGHLIGHT_COLOR_LABEL")}</span>
                        </div>
                    </div>
                </TrebbleCard>

                {/* Layout Card */}
                <TrebbleCard size="small" title={window.getI18n(ti18n, "LAYOUT_TITLE")} className="mt-4">
                    <FieldContainer label={window.getI18n(ti18n, "MAX_LINES_LABEL")}>
                        <TrebbleSelect
                            className="w-full"
                            options={Object.values(CAPTION_LINE_CONFIG)}
                            value={localCaption.captionConfig.maxLines}
                            onChange={(newLineConfig) => handleFieldChange('maxLines', newLineConfig)}
                        />
                    </FieldContainer>

                    <FieldContainer label={window.getI18n(ti18n, "POSITION_LABEL")}>
                    <TrebbleRadio.Group
                        block
                        optionType="button"
                        buttonStyle="solid"
                        options={Object.values(CAPTION_POSITIONS)}
                        value={localCaption.captionConfig?.position.split(" ")[0]}
                        onChange={(e: TrebbleRadioChangeEvent) => handleFieldChange('position', localCaption.captionConfig.position.replace(/^\S+/, e.target.value) as Position)}
                    />
                </FieldContainer>

                    <FieldContainer label={window.getI18n(ti18n, "ALIGN_LABEL")}>
                        <TrebbleSelect
                            className="w-full"
                            options={Object.values(CAPTION_ALIGNMENTS)}
                            value={localCaption.captionConfig.position.split(" ")[1]}
                            onChange={(newAlignment) => handleFieldChange('position', localCaption.captionConfig.position.replace(/\S+$/, newAlignment) as Position)}
                        />
                    </FieldContainer>
                </TrebbleCard>

                {/* Animation & Text Card */}
                <TrebbleCard size="small" title={window.getI18n(ti18n, "ANIMATION_TEXT_TITLE")} className="mt-4">
                    <FieldContainer label={window.getI18n(ti18n, "TEXT_ANIMATION_LABEL")}>
                        <TrebbleSelect
                            className="w-full"
                            options={Object.values(CAPTION_ANIMATIONS)}
                            value={localCaption.captionConfig.textAnimation}
                            onChange={(newAnimation) => handleFieldChange('textAnimation', newAnimation)}
                        />
                    </FieldContainer>

                    <FieldContainer label={window.getI18n(ti18n, "CASE_LABEL")}>
                        <TrebbleRadio.Group
                            block
                            optionType="button"
                            buttonStyle="solid"
                            options={Object.values(CAPTION_CASES)}
                            value={localCaption.captionConfig.captionCase}
                            onChange={(newCase: TrebbleRadioChangeEvent) => handleFieldChange('captionCase', newCase.target.value)}
                        />
                    </FieldContainer>

                    <FieldContainer label={window.getI18n(ti18n, "HIDE_PUNCTUATION_LABEL")} direction="row">
                        <div className="flex justify-end">
                            <TrebbleSwitch
                                size="small"
                                value={localCaption.captionConfig.hidePunctuation}
                                checked={localCaption.captionConfig.hidePunctuation}
                                onChange={(newVal: boolean) => handleFieldChange('hidePunctuation', newVal)}
                            />
                        </div>
                    </FieldContainer>
                </TrebbleCard>
            </div>
        </div>
    );
}; 